import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { CodeStatus, EmbeddedCodeLike, EntitySubStatus } from '@jms/model';
import { BadgeComponent } from '@workbuddy/ui/components/badge';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { TooltipModule } from 'primeng/tooltip';
import { resolveEffectiveStatus, resolveSorNormalStyle, resolveStatusStyle } from '../../support/item.status.style';
import { itemStatusCellBridgeStyles, itemStatusCellStyleId } from './item.status.cell.renderer.component.theme';

/** `isLocked` is the contractor portal's; the office mounts never set it. */
type ItemStatusCellParams = ICellRendererParams & { outlined?: boolean; isLocked?: (row: unknown) => boolean };

/**
 * Status cell for the items boards: the status pill alone. Finance links are counted by the
 * indicators cell beside it, which owns the "Financial Documents" card (WBS-12164).
 *
 * Items rows carry the raw CodeStatus, so the pill styling is resolved here instead of read off the row.
 *
 * `outlined` (set on the office mounts and every SOR mount) draws the outlined Normal chip on one
 * shrinkable row; the portal Items boards keep the filled pill.
 */
@Component({
  selector: 'item-status-cell-renderer',
  templateUrl: './item.status.cell.renderer.component.html',
  styleUrls: ['./item.status.cell.renderer.component.less'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BadgeComponent, TooltipModule],
})
export class ItemStatusCellRendererComponent implements ICellRendererAngularComp {

  protected readonly $status = signal<CodeStatus | null>(null);
  protected readonly $itemStatus = signal<EntitySubStatus | null>(null);
  protected readonly $outlined = signal(false);

  protected readonly $locked = signal(false);

  /** Entity-status name when stamped (SOR / pending); otherwise the legacy CodeStatus string. */
  protected readonly $statusLabel = computed(() => this.$itemStatus()?.name ?? this.$status() ?? '');

  protected readonly $statusStyle = computed(() => {

    const style = resolveStatusStyle(this.$status(), this.$itemStatus());
    const isNormal = resolveEffectiveStatus(this.$status(), this.$itemStatus()) === CodeStatus.Normal;

    return this.$outlined() && isNormal ? resolveSorNormalStyle(this.$itemStatus()) : style;
  });

  public constructor() {

    DesignSystem.Mount(inject(DOCUMENT), itemStatusCellStyleId, itemStatusCellBridgeStyles);
  }

  public agInit(params: ItemStatusCellParams): void {

    this.read(params);
  }

  public refresh(params: ItemStatusCellParams): boolean {

    this.read(params);
    return true;
  }

  private read(params: ItemStatusCellParams): void {

    // JobItem rows stay parent-shaped after unwind (no $replaceRoot): the item lives at the
    // column prefix (e.g. field `workOrders.codes.status` → item at `workOrders.codes`).
    const item = resolveItemFromParams(params);

    // Read status from the row, not params.value: the column's valueGetter (itemStatusCellValue)
    // returns the effective-status label so AG refreshes the pill on a pending-status change, so
    // params.value is no longer the raw CodeStatus this pill styles by.
    this.$status.set(item?.status ?? null);
    this.$itemStatus.set(item?.itemStatus ?? null);
    this.$outlined.set(!!params.outlined);
    this.$locked.set(params.isLocked?.(params.data) === true);
  }
}

/**
 * Column value for the status cell — the status label plus every sibling fact the cell draws. AG's
 * immutable row-diff calls refresh() only when this value changes.
 */
export function itemStatusCellValue(params: { data?: unknown; colDef?: { field?: string; cellRendererParams?: Pick<ItemStatusCellParams, 'isLocked'> } }): string {

  const item = resolveItemFromParams(params);
  if (!item) return '';

  const label = item.itemStatus?.name ?? (item.status as string | undefined) ?? '';
  // The pill draws the saved colour, so a re-stamp that keeps the name but changes the colour must still refresh.
  const color = item.itemStatus?.color ?? '';
  // A capability can flip without a status change; the term is appended only when locked so an office row's value is unchanged.
  const locked = params.colDef?.cellRendererParams?.isLocked?.(params.data) === true ? ['locked'] : [];
  return [label, color, ...locked].join('|');
}

/** Group key for the status columns; grouping on the valueGetter string split one status by its colour and lock. */
export function itemStatusGroupKey(params: { data?: unknown; colDef?: { field?: string } }): string {

  const item = resolveItemFromParams(params);
  const status = (item?.status as string | undefined) ?? '';

  if (params.colDef?.field?.endsWith('itemStatus')) return item?.itemStatus?._id ?? status;

  return status;
}

/** Item object for the status fields — parent-shaped chain path or flat EmbeddedCode row. */
function resolveItemFromParams(params: { data?: unknown; colDef?: { field?: string } } | undefined): EmbeddedCodeLike | undefined {

  const data = params?.data;
  if (!data) return undefined;

  const fieldPath = params.colDef?.field ?? '';
  if (!fieldPath.includes('.')) return data as EmbeddedCodeLike;

  // Drop the leaf (status / itemStatus) so the remainder is the embedded item path.
  const itemPath = fieldPath.split('.').slice(0, -1).join('.');
  if (!itemPath) return data as EmbeddedCodeLike;

  const item = itemPath.split('.').reduce<unknown>((acc, part) => (acc as Record<string, unknown> | null | undefined)?.[part], data);
  return (item as EmbeddedCodeLike | undefined) ?? undefined;
}
